import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import componentStyles from './idblab-numbered-card.css.lit';
import { IdbLabContentCard } from '../idblab-content-card/idblab-content-card';
import '../../idb/idb-truncate-text/idb-truncate-text';
import { rowBackgroundPropertyData } from '../../../data_idblab';
import '../../idb/idb-truncate-text/idb-truncate-text';
import { LinkTargetType } from 'src/components/base/outline-link/config';

export type cardBackgrounds = typeof rowBackgroundPropertyData;

export type NumberColors = 'green' | 'magenta' | 'white';

/**
 * The IdbLabNumberedCard component
 * @element idblab-numbered-card
 * @slot title - The title slot
 * @slot content - The content slot
 */
@customElement('idblab-numbered-card')
export class IdbLabNumberedCard extends IdbLabContentCard {
  static styles: CSSResultGroup = [IdbLabContentCard.styles, componentStyles];
  /**
   * The number to display in the card.
   */
  @property({ type: String, attribute: 'number' })
  number = '01';

  /**
   * The button target for the link; Defaulting to _blank to prevent integration rework
   */
  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType = '_blank';

  /**
   * Number Color options
   */
  @property({ type: String, attribute: 'number-color' })
  numberColor: NumberColors;

  /**
   * Modal window
   */
  @property({ type: Boolean, reflect: true, attribute: 'modal-enabled' })
  modal: false;

  @state() hasModalSlot: boolean;

  connectedCallback() {
    super.connectedCallback();

    this.imagePosition = 'top';
    this.contentMode = 'compact';
  }

  firstUpdated(): void {
    super.firstUpdated();
    this.hasModalSlot = this.slots.test('modal');
  }

  openModalIfAvailable(e: Event): void {
    if (this.modal) {
      e.preventDefault()
      const modalElement = this.shadowRoot?.querySelector('idblab-modal');

      if (modalElement) {
        // @ts-ignore
        modalElement.open();
      }
    }
  }

  render(): TemplateResult {
    const cardClasses = {
      'card': true,
      [`image-position--${this.imagePosition}`]: this.imagePosition,
      'outline-shadow': true,
      'outline-rounded-2xl': this.isRounded,
      [`outline-bg-${this.backgroundColor}`]:
        this.backgroundColor !== undefined,
      [`content-mode--${this.contentMode}`]: this.contentMode,
    };
    const imageClasses = {
      'card-image': true,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
      [`content-alignment--${this.contentAlignment}`]: this.contentAlignment,
    };
    const titleClasses = {
      'card-title': true,
      [`title-alignment--${this.titleAlignment}`]: this.titleAlignment,
      [`title-mode--${this.titleMode}`]: this.titleMode,
    };
    const contentClasses = {
      'card-content': true,
      'clamp': true,
    };
    const numberClasses = {
      'card-number': true,
      [`card-number--${this.numberColor}`]: this.numberColor !== undefined,
    };

    const content = html`
      <section class="${classMap(cardClasses)}">
        ${this.imageTemplate(imageClasses)}
        <section class="${classMap(contentWrapperClasses)}">
          <p class="${classMap(numberClasses)}">${this.number}</p>
          <span>
            ${this.titleTemplate(titleClasses)}
            ${this.contentTemplate(contentClasses)}
          </span>
        </section>
      </section>
    `;

    if (this.modal || this.url) {
      return html`
      ${this.modalTemplate()}
      <a href="${this.url}" target="${this.linkTarget}" @click="${this.openModalIfAvailable}">
        ${content}
     </a>
    `;
    } else {
      return html`
        ${content}
      `;
    }

  }

  /**
   * Template partial for the Image slot.
   * @param classes
   * @returns TemplateResult | null
   */
  imageTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasImageSlot) return null;

    return html` <section class="${classMap(classes)}">
       ${this.slotsFancy.renderInShadow('image')}
      </section>`;
  }

  /**
   * Template partial for the Title slot.
   * @param classes
   * @returns TemplateResult | null
   */
  titleTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasTitleSlot) return null;

    return html` <section class="${classMap(classes)}">
        ${this.slotsFancy.renderInShadow('title')}
      </section>`;
  }

  /**
   * Template partial for the Modal slot.
   * @returns TemplateResult | null
   */
  modalTemplate(): TemplateResult | null {
    if (!this.hasModalSlot) return null;

    return html`<idblab-modal>
      ${this.slotsFancy.renderInShadow('modal')}
    </idblab-modal>`;
  }
}
